/** Daily activity log (shift report): the deputy's log for a shift date, the supervisor's review list, and one log. */
import { useState } from 'react';
import { api } from '../shared/api.ts';
import { useRouter } from '../shared/router.tsx';
import { Badge, Card, ErrorBox, Notice, PageHeader, Spinner, useAction, useLoad } from '../shared/ui.tsx';
import { at, EmptyState, when } from './common.tsx';

const STATUS: Record<string, [string, 'neutral' | 'info' | 'ok' | 'bad']> = { open: ['Not submitted', 'neutral'], submitted: ['With supervisor', 'info'], approved: ['Approved', 'ok'], returned: ['Sent back', 'bad'] };
const longDay = (d: string) => new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
const shortDay = (d: string) => new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
const time = (iso: string) => new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
const shiftDay = (d: string, by: number) => {
  const x = new Date(`${d}T12:00:00`);
  x.setDate(x.getDate() + by);
  return x.toISOString().slice(0, 10);
};
const afterMidnight = (iso: string, date: string) => new Date(iso).toLocaleDateString('en-CA') > date;

function Entries({ rows, kinds, date, edit, onChange }: { rows: any[]; kinds: Record<string, string>; date: string; edit: boolean; onChange?: () => void }) {
  const a = useAction();
  const [editing, setEditing] = useState<string | null>(null);
  const [f, setF] = useState<any>({});
  if (!rows.length) return <p className="muted">Nothing on the log yet.</p>;
  return (
    <div className="table-wrap">
      <ErrorBox error={a.error} />
      <table className="table dl-table">
        <thead>
          <tr>
            <th>Time</th>
            <th>What</th>
            <th>Where</th>
            <th>Case #</th>
            <th>Details</th>
            {edit && <th className="no-print" />}
          </tr>
        </thead>
        <tbody>
          {rows.map((e) =>
            editing === e.id ? (
              <tr key={e.id}>
                <td>
                  <input type="time" value={f.time} onChange={(x) => setF({ ...f, time: x.target.value })} aria-label="Time" />
                </td>
                <td>
                  <select value={f.kind} onChange={(x) => setF({ ...f, kind: x.target.value })} aria-label="What">
                    {Object.entries(kinds).map(([k, l]) => (
                      <option key={k} value={k}>
                        {l}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <input value={f.location ?? ''} onChange={(x) => setF({ ...f, location: x.target.value })} aria-label="Where" />
                </td>
                <td>
                  <input value={f.caseNumber ?? ''} onChange={(x) => setF({ ...f, caseNumber: x.target.value })} aria-label="Case number" />
                </td>
                <td>
                  <input value={f.description} onChange={(x) => setF({ ...f, description: x.target.value })} aria-label="Details" />
                </td>
                <td className="no-print">
                  <div className="row gap-sm">
                    <button className="btn sm primary" disabled={a.busy} onClick={() => a.run(async () => (await api.post(`/api/daily-logs/entries/${e.id}`, { ...f, nextDay: afterMidnight(e.at, date) }), setEditing(null), onChange?.()))}>
                      Save
                    </button>
                    <button className="btn sm ghost" onClick={() => setEditing(null)}>
                      Cancel
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              <tr key={e.id}>
                <td className="mono">
                  {time(e.at)}
                  {afterMidnight(e.at, date) && <div className="small muted">next day</div>}
                </td>
                <td>{kinds[e.kind] ?? e.kind}</td>
                <td>{e.location}</td>
                <td className="mono">{e.case_number}</td>
                <td>{e.description}</td>
                {edit && (
                  <td className="no-print">
                    <div className="row gap-sm">
                      <button className="btn sm ghost" onClick={() => (setEditing(e.id), setF({ time: time(e.at), kind: e.kind, location: e.location, caseNumber: e.case_number, description: e.description }))}>
                        Edit
                      </button>
                      <button className="btn sm ghost" aria-label="Remove line" disabled={a.busy} onClick={() => window.confirm('Remove this line?') && a.run(async () => (await api.post(`/api/daily-logs/entries/${e.id}/delete`), onChange?.()))}>
                        ✕
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ),
          )}
        </tbody>
      </table>
    </div>
  );
}

function Header({ log, date, edit, onDone }: { log: any; date: string; edit: boolean; onDone: () => void }) {
  const [f, setF] = useState({ unitNumber: log?.unit_number ?? '', startMiles: log?.start_miles ?? '', endMiles: log?.end_miles ?? '', notes: log?.notes ?? '' });
  const a = useAction();
  const miles = f.startMiles !== '' && f.endMiles !== '' ? Number(f.endMiles) - Number(f.startMiles) : null;
  if (!edit)
    return (
      <dl className="kv">
        <dt>Car</dt>
        <dd>{log.unit_number ?? '—'}</dd>
        <dt>Mileage</dt>
        <dd>{log.start_miles != null ? `${Number(log.start_miles).toLocaleString()} – ${log.end_miles != null ? Number(log.end_miles).toLocaleString() : '?'}${log.end_miles != null ? ` (${(log.end_miles - log.start_miles).toLocaleString()} mi)` : ''}` : '—'}</dd>
        {log.notes && (
          <>
            <dt>Notes</dt>
            <dd style={{ whiteSpace: 'pre-wrap' }}>{log.notes}</dd>
          </>
        )}
      </dl>
    );
  return (
    <div className="stack">
      <div className="grid-3">
        <label>
          Car #
          <input value={f.unitNumber} onChange={(e) => setF({ ...f, unitNumber: e.target.value })} />
        </label>
        <label>
          Starting mileage
          <input type="number" inputMode="numeric" value={f.startMiles} onChange={(e) => setF({ ...f, startMiles: e.target.value })} />
        </label>
        <label>
          Ending mileage
          <input type="number" inputMode="numeric" value={f.endMiles} onChange={(e) => setF({ ...f, endMiles: e.target.value })} />
        </label>
      </div>
      {miles != null && miles >= 0 && <p className="small muted">{miles.toLocaleString()} miles this shift</p>}
      <label>
        Notes for the shift
        <textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="Anything else your supervisor should know" />
      </label>
      <ErrorBox error={a.error} />
      <div>
        <button className="btn" disabled={a.busy} onClick={() => a.run(async () => (await api.post('/api/daily-logs/me', { date, unitNumber: f.unitNumber || null, startMiles: f.startMiles === '' ? null : Number(f.startMiles), endMiles: f.endMiles === '' ? null : Number(f.endMiles), notes: f.notes || null }), onDone()))}>
          Save car and mileage
        </button>
      </div>
    </div>
  );
}

function AddLine({ date, kinds, onDone }: { date: string; kinds: Record<string, string>; onDone: () => void }) {
  const now = new Date();
  const blank = () => ({ time: `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`, kind: 'call', location: '', caseNumber: '', description: '', nextDay: false });
  const [f, setF] = useState(blank);
  const a = useAction();
  return (
    <Card title="Add a line">
      <div className="grid-3">
        <label>
          Time
          <input type="time" value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} />
        </label>
        <label>
          What
          <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}>
            {Object.entries(kinds).map(([k, l]) => (
              <option key={k} value={k}>
                {l}
              </option>
            ))}
          </select>
        </label>
        <label>
          Where
          <input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} />
        </label>
        <label>
          Case #
          <input value={f.caseNumber} onChange={(e) => setF({ ...f, caseNumber: e.target.value })} />
        </label>
      </div>
      <label>
        Details
        <input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="What you did" />
      </label>
      <label className="check">
        <input type="checkbox" checked={f.nextDay} onChange={(e) => setF({ ...f, nextDay: e.target.checked })} /> After midnight (night shift)
      </label>
      <ErrorBox error={a.error} />
      <div>
        <button className="btn primary" disabled={a.busy} onClick={() => a.run(async () => (await api.post('/api/daily-logs/me/entries', { ...f, date }), setF(blank()), onDone()))}>
          Add line
        </button>
      </div>
    </Card>
  );
}

export function MyDailyLogPage() {
  const { navigate } = useRouter();
  const [date, setDate] = useState<string | null>(() => new URLSearchParams(location.search).get('date'));
  const r = useLoad(() => api.get<any>(`/api/daily-logs/me${date ? `?date=${date}` : ''}`), [date]);
  const a = useAction();
  if (!r.data) return r.error ? <ErrorBox error={r.error} /> : <Spinner />;
  const { log, entries, activity, kinds, recent } = r.data;
  const d: string = r.data.date;
  const edit = !log || log.status === 'open' || log.status === 'returned';
  const today = new Date().toLocaleDateString('en-CA');
  const go = (x: string) => (setDate(x), navigate(at(`/daily-log?date=${x}`), true));
  return (
    <div className="ops stack">
      <PageHeader
        crumb="Me"
        title="Daily activity log"
        sub={longDay(d)}
        actions={
          <div className="row gap-sm no-print">
            <button className="btn" onClick={() => window.print()}>
              Print
            </button>
            {edit && (
              <button className="btn primary" disabled={a.busy || (!entries.length && !log?.notes)} onClick={() => window.confirm('Submit this log to your supervisor?') && a.run(async () => (await api.post('/api/daily-logs/me/submit', { date: d }), r.reload()))}>
                Submit to supervisor
              </button>
            )}
          </div>
        }
      />
      <div className="row gap-sm no-print">
        <button type="button" className="btn sm ghost" aria-label="Day before" onClick={() => go(shiftDay(d, -1))}>
          ‹
        </button>
        <input type="date" value={d} max={today} onChange={(e) => e.target.value && go(e.target.value)} aria-label="Shift date" style={{ maxWidth: 170 }} />
        <button type="button" className="btn sm ghost" aria-label="Day after" disabled={d >= today} onClick={() => go(shiftDay(d, 1))}>
          ›
        </button>
        {log && <Badge tone={STATUS[log.status]![1]}>{STATUS[log.status]![0]}</Badge>}
      </div>
      <ErrorBox error={a.error} />
      {log?.status === 'returned' && (
        <Notice tone="bad">
          <strong>Sent back:</strong> {log.review_note}
        </Notice>
      )}
      {log?.status === 'submitted' && <Notice tone="info">Submitted {when(log.submitted_at)}. Your supervisor can send it back if something needs fixing.</Notice>}
      {log?.status === 'approved' && <Notice tone="ok">Approved {when(log.reviewed_at)}.</Notice>}
      {edit && activity.length > 0 && (
        <Card
          title={`From your day in BlueHQ (${activity.length})`}
          actions={
            <button className="btn sm primary" disabled={a.busy} onClick={() => a.run(async () => (await api.post('/api/daily-logs/me/pull', { date: d }), r.reload()))}>
              Add all to the log
            </button>
          }
        >
          <ul className="plain small">
            {activity.map((x: any) => (
              <li key={x.ref_id}>
                <span className="mono">{time(x.at)}</span> · {x.description}
                {x.location ? ` · ${x.location}` : ''}
              </li>
            ))}
          </ul>
        </Card>
      )}
      <Card title="Car and mileage">
        <Header key={`${d}-${log?.updated_at ?? ''}`} log={log} date={d} edit={edit} onDone={r.reload} />
      </Card>
      <Card title={`The log (${entries.length})`}>
        <Entries rows={entries} kinds={kinds} date={d} edit={edit} onChange={r.reload} />
      </Card>
      {edit && <AddLine date={d} kinds={kinds} onDone={r.reload} />}
      {recent.length > 0 && (
        <Card title="Your recent logs" className="no-print">
          <ul className="plain">
            {recent.map((x: any) => (
              <li key={x.id}>
                <button type="button" className="btn link" onClick={() => go(x.shift_date)}>
                  {shortDay(x.shift_date)}
                </button>{' '}
                <Badge tone={STATUS[x.status]![1]}>{STATUS[x.status]![0]}</Badge>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

export function DailyLogsPage() {
  const { navigate } = useRouter();
  const [status, setStatus] = useState('submitted');
  const [date, setDate] = useState('');
  const [q, setQ] = useState('');
  const r = useLoad(() => api.get<any>(`/api/daily-logs?status=${status}&date=${date}&q=${encodeURIComponent(q)}`), [status, date, q]);
  return (
    <div className="ops stack">
      <PageHeader crumb="Supervision" title="Activity logs" sub="Deputies’ daily activity logs. Approve them, or send one back with what needs fixing." />
      <div className="row gap-sm wrap">
        <div className="seg-ctl" role="tablist" aria-label="Which logs">
          {(
            [
              ['submitted', 'Waiting on review'],
              ['returned', 'Sent back'],
              ['approved', 'Approved'],
              ['all', 'All'],
            ] as const
          ).map(([k, l]) => (
            <button key={k} type="button" role="tab" aria-selected={status === k} className={status === k ? 'on' : ''} onClick={() => setStatus(k)}>
              {l}
            </button>
          ))}
        </div>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} aria-label="Shift date" style={{ maxWidth: 170 }} />
        <input type="search" placeholder="Deputy, call #, car…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search logs" style={{ maxWidth: 220 }} />
      </div>
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : !r.data.logs.length ? (
        <EmptyState icon="check" title={status === 'submitted' ? 'Nothing waiting' : 'None'} />
      ) : (
        <div className="card table-wrap">
          <table className="table clickable">
            <thead>
              <tr>
                <th>Shift</th>
                <th>Deputy</th>
                <th>Car / miles</th>
                <th>Lines</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {r.data.logs.map((l: any) => (
                <tr key={l.id} onClick={() => navigate(at(`/daily-log/${l.id}`))}>
                  <td>{shortDay(l.shift_date)}</td>
                  <td>
                    <strong>{l.name}</strong>
                    <div className="small muted">{[l.call_number, l.unit].filter(Boolean).join(' · ')}</div>
                  </td>
                  <td className="small">
                    {l.unit_number ?? '—'}
                    {l.start_miles != null && l.end_miles != null ? ` · ${(l.end_miles - l.start_miles).toLocaleString()} mi` : ''}
                  </td>
                  <td>{l.entries}</td>
                  <td>
                    <Badge tone={STATUS[l.status]![1]}>{STATUS[l.status]![0]}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function DailyLogView({ id }: { id: string }) {
  const { navigate } = useRouter();
  const r = useLoad(() => api.get<any>(`/api/daily-logs/${id}`), [id]);
  const a = useAction();
  if (!r.data) return r.error ? <ErrorBox error={r.error} /> : <Spinner />;
  const { log, entries, kinds } = r.data;
  const back = () => {
    const note = window.prompt('What needs fixing?');
    if (!note) return;
    a.run(async () => (await api.post(`/api/daily-logs/${id}/review`, { action: 'return', note }), r.reload()));
  };
  return (
    <div className="ops stack">
      <PageHeader
        crumb="Activity logs"
        title={`${log.name}${log.call_number ? ` (${log.call_number})` : ''}`}
        sub={`Daily activity log · ${longDay(log.shift_date)}`}
        actions={
          <div className="row gap-sm no-print">
            <button className="btn" onClick={() => window.print()}>
              Print
            </button>
            {r.data.own && (log.status === 'open' || log.status === 'returned') && (
              <button className="btn primary" onClick={() => navigate(at(`/daily-log?date=${log.shift_date}`))}>
                Edit my log
              </button>
            )}
            {r.data.canReview && (
              <>
                <button className="btn" disabled={a.busy} onClick={back}>
                  Send back
                </button>
                <button className="btn primary" disabled={a.busy} onClick={() => a.run(async () => (await api.post(`/api/daily-logs/${id}/review`, { action: 'approve' }), r.reload()))}>
                  Approve
                </button>
              </>
            )}
          </div>
        }
      />
      <ErrorBox error={a.error} />
      <div className="row gap-sm">
        <Badge tone={STATUS[log.status]![1]}>{STATUS[log.status]![0]}</Badge>
        {log.submitted_at && <span className="small muted">Submitted {when(log.submitted_at)}</span>}
        {log.reviewed_at && (
          <span className="small muted">
            · {log.status === 'approved' ? 'Approved' : 'Reviewed'} by {log.reviewed_by_name} {when(log.reviewed_at)}
          </span>
        )}
      </div>
      {log.status === 'returned' && log.review_note && <Notice tone="bad">Sent back: {log.review_note}</Notice>}
      <Card title="Car and mileage">
        <Header log={log} date={log.shift_date} edit={false} onDone={() => undefined} />
      </Card>
      <Card title={`The log (${entries.length})`}>
        <Entries rows={entries} kinds={kinds} date={log.shift_date} edit={false} />
      </Card>
    </div>
  );
}
